import { ChangeDetectionStrategy, Component, Input, TemplateRef, ViewChild } from '@angular/core';

import { EpcotConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ThemeOptions } from '@frontend/sports/common/ui/shared';
import { DsButtonKind, DsButtonVariant } from '@frontend/ui/button';
import { Store } from '@ngrx/store';
import { BetBuilderConfigService } from 'packages/sports/web/app/src/bet-builder/services/bet-builder-config.service';
import { PrecreatedBabTrackingService } from 'packages/sports/web/app/src/option-group/precreated-bab-tracking.service';

import { BetslipBetBuilderPick } from '../../../core/picks/betslip-bet-builder-pick';
import { BetslipGroupPick } from '../../../core/picks/betslip-group-pick';
import { BetBuilderUtilService } from '../../bet-builder/services/bet-builder-util.service';
import BetslipPickActions from '../../picks/actions';

@Component({
    selector: 'bs-betslip-betbuilder-edit-pick-button',
    templateUrl: './betslip-betbuilder-edit-pick-button.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./betslip-betbuilder-edit-pick-button.component.scss'],
    standalone: false,
})
export class BetslipBetbuilderEditPickButtonComponent {
    @Input() pick: BetslipBetBuilderPick | BetslipGroupPick;
    @Input() hideIcon: boolean;
    @Input() isLinear: boolean;
    @Input() title: string | undefined;
    @ViewChild('header') headerTemplate: TemplateRef<any>;

    isEpcotEnabled: boolean;
    editIconButtonOverride = false;

    get eventStartDate(): Date {
        return BetslipGroupPick.isPick(this.pick) ? this.pick.eventDate : this.pick.event.event.date;
    }

    constructor(
        public siteCore: Sitecore,
        private store: Store,
        private betBuilderUtilService: BetBuilderUtilService,
        private betBuilderConfigService: BetBuilderConfigService,
        private trackingService: PrecreatedBabTrackingService,
        epcotConfig: EpcotConfig,
    ) {
        this.isEpcotEnabled = epcotConfig.isEnabled;
        this.editIconButtonOverride = this.betBuilderConfigService.editIconButtonOverride();
    }

    async click(event: MouseEvent): Promise<void> {
        event.stopPropagation();
        event.preventDefault();

        if (!this.pick.isOpen()) {
            return;
        }

        if (!BetslipBetBuilderPick.isEntainUiBetBuilderPick(this.pick) && !BetslipGroupPick.isPick(this.pick)) {
            this.store.dispatch(
                BetslipPickActions.checkPrecreatedBABConversion({ pickId: this.pick.id, headerTemplate: this.headerTemplate, isBetslip: true }),
            );
        } else {
            if (this.betBuilderUtilService.isBetBuilderOverlayOpen(this.pick)) {
                await this.betBuilderUtilService.closeOverlay();
            }
            this.betBuilderUtilService.navigateToBABTab(this.pick, this.headerTemplate);
        }

        this.trackingService.trackAddorEditClick(
            this.pick.eventName.name,
            this.pick.sportId,
            this.pick.competitionId,
            this.siteCore.betBuilderEntainUI.messages?.EditPickButton ?? '',
        );
    }

    get participants() {
        return 'event' in this.pick ? this.pick.event.event?.participants : [];
    }

    get eventViewType() {
        return 'event' in this.pick ? this.pick.event.event?.viewType : undefined;
    }

    get buttonConfig(): {
        kind: DsButtonKind;
        variant: DsButtonVariant;
    } {
        return this.isLinear
            ? {
                  kind: 'utility',
                  variant: 'flat-reduced',
              }
            : {
                  kind: 'tertiary',
                  variant: 'outline',
              };
    }

    get buttonThemeKind(): ThemeOptions<DsButtonKind> {
        return this.isLinear
            ? { 'mgm-4': 'utility', 'sports-interaction': 'secondary' }
            : { 'mgm-4': 'secondary', 'sports-interaction': 'secondary' };
    }

    get buttonThemeVariant(): ThemeOptions<DsButtonVariant> {
        return this.isLinear ? { 'sports-interaction': 'outline' } : {};
    }
}
